import { useState } from '@wordpress/element';
import HouseSvgIcon from './HouseSVG.svg';

const CATEGORIES = [
	{ label: 'A', min: 0, max: 10, color: '#2d8a2d', textColor: '#fff' },
	{ label: 'B', min: 10, max: 50, color: '#4aab2e', textColor: '#fff' },
	{ label: 'C', min: 51, max: 100, color: '#8dc63f', textColor: '#fff' },
	{ label: 'D', min: 101, max: 150, color: '#f5c000', textColor: '#fff' },
	{ label: 'E', min: 151, max: 200, color: '#f08000', textColor: '#fff' },
	{ label: 'F', min: 201, max: 250, color: '#e05000', textColor: '#fff' },
	{ label: 'G', min: 251, max: 9999, color: '#cc1400', textColor: '#fff' },
];

const BASE_WIDTH = 180;
const WIDTH_STEP = 22;
const BAR_HEIGHT = 44;
const ARROW_TIP = 18;
const GAP = 5;

function HouseSVG( iconPath ) {
	return (
		<img
			style={ { width: '100%', height: '100%' } }
			src={ iconPath.length ? iconPath : HouseSvgIcon }
		/>
	);
}

function ScaleBar( { category, index, isActive, markerValue } ) {
	const width = BASE_WIDTH + index * WIDTH_STEP;
	const totalWidth = BASE_WIDTH + 6 * WIDTH_STEP + ARROW_TIP;

	return (
		<div
			style={ {
				position: 'relative',
				height: BAR_HEIGHT,
				marginBottom: GAP,
				display: 'flex',
				alignItems: 'center',
			} }
		>
			{ /* Arrow bar (clip-path chevron) */ }
			<div
				style={ {
					position: 'relative',
					width: width + ARROW_TIP,
					height: BAR_HEIGHT,
					background: category.color,
					clipPath: `polygon(0 0, ${ width }px 0, ${
						width + ARROW_TIP
					}px 50%, ${ width }px 100%, 0 100%)`,
					display: 'flex',
					alignItems: 'center',
					flexShrink: 0,
				} }
			>
				{ /* Category letter */ }
				<span
					style={ {
						color: category.textColor,
						fontFamily: "'Barlow', 'Arial Black', sans-serif",
						fontWeight: 800,
						fontSize: 22,
						marginLeft: 14,
						letterSpacing: 1,
						userSelect: 'none',
					} }
				>
					{ category.label }
				</span>

				{ /* Range label */ }
				<span
					style={ {
						color: 'rgba(255,255,255,0.88)',
						fontFamily: "'Barlow', Arial, sans-serif",
						fontWeight: 500,
						fontSize: 11,
						marginLeft: 8,
						whiteSpace: 'nowrap',
					} }
				>
					{ category.min }–
					{ category.max === 250 ? '>160' : category.max } kWh/m²a
				</span>
			</div>

			{ /* Marker triangle if this bar is active */ }
			{ isActive && (
				<div
					style={ {
						position: 'absolute',
						left: getMarkerPosition(
							category,
							markerValue,
							width + ARROW_TIP,
							totalWidth
						),
						top: '50%',
						transform: 'translate(-50%, -50%)',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						zIndex: 10,
						pointerEvents: 'none',
					} }
				>
					{ /* Triangle pointer */ }
					<div
						style={ {
							width: 0,
							height: 0,
							borderLeft: '9px solid transparent',
							borderRight: '9px solid transparent',
							borderTop: '13px solid #1a1a2e',
							marginBottom: 0,
						} }
					/>
					{ /* Value bubble */ }
					<div
						style={ {
							background: '#1a1a2e',
							color: '#fff',
							fontFamily: "'Barlow', Arial, sans-serif",
							fontWeight: 700,
							fontSize: 11,
							padding: '2px 6px',
							borderRadius: 4,
							whiteSpace: 'nowrap',
							boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
						} }
					>
						{ markerValue } kWh/m²a
					</div>
				</div>
			) }
		</div>
	);
}

function getMarkerPosition( category, value, barWidthWithArrow, totalWidth ) {
	// Position within the bar proportional to value in range
	const pct = ( value - category.min ) / ( category.max - category.min );
	const clampedPct = Math.min( Math.max( pct, 0.05 ), 0.85 );
	// Map into the bar area (excluding arrow tip)
	const labelOffset = 100; // skip label area
	const usableWidth = barWidthWithArrow - ARROW_TIP - labelOffset;
	return labelOffset + clampedPct * usableWidth;
}

export default function EnergyScale( {
	value = 85,
	showIcon = 0,
	iconPath = '',
} ) {
	const [ demoValue, setDemoValue ] = useState( value );

	const activeIndex = CATEGORIES.findIndex(
		( c ) => demoValue >= c.min && demoValue <= c.max
	);

	return (
		<div
			style={ {
				fontFamily: "'Barlow', Arial, sans-serif",
				background: '#f7f8fa',
				minHeight: '100vh',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 32,
			} }
		>
			<h2
				style={ {
					fontFamily: "'Barlow Condensed', 'Arial Black', sans-serif",
					fontWeight: 800,
					fontSize: 22,
					letterSpacing: 2,
					color: '#1a1a2e',
					marginBottom: 28,
					textTransform: 'uppercase',
					textAlign: 'center',
				} }
			>
				Energieeffizienzklasse
			</h2>

			<div
				style={ {
					display: 'flex',
					alignItems: 'flex-end',
					gap: 24,
					background: '#fff',
					borderRadius: 16,
					boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
					padding: '32px 36px',
				} }
			>
				{ /* House SVG — left side, aligned to bottom of scale */ }
				{ !! showIcon && (
					<div
						style={ {
							width: 140,
							height:
								CATEGORIES.length * ( BAR_HEIGHT + GAP ) - GAP,
							flexShrink: 0,
							alignSelf: 'center',
						} }
					>
						<HouseSVG iconPath={ iconPath } />
					</div>
				) }

				{ /* Scale bars */ }
				<div style={ { display: 'flex', flexDirection: 'column' } }>
					{ CATEGORIES.map( ( cat, i ) => (
						<ScaleBar
							key={ cat.label }
							category={ cat }
							index={ i }
							isActive={ i === activeIndex }
							markerValue={ demoValue }
						/>
					) ) }
				</div>
			</div>
		</div>
	);
}
